import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { TextInput } from "@/components/design/TextField";
import { PersonLink } from "@/components/common/PersonLink";
import { AccessGroupPeopleDocument, StringFilterCaseSensitivity, type AccessGroupPeopleQuery } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

export type InspectionPerson = AccessGroupPeopleQuery["persons"]["nodes"][number];

export function AccessInspectionPersonPicker({ id, person, onSelect, excludedId, label }: {
  id: string; person: InspectionPerson | null; onSelect: (person: InspectionPerson | null) => void; excludedId?: string; label: string;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => { const timer = setTimeout(() => setTerm(search.trim()), 250); return () => clearTimeout(timer); }, [search]);
  const { data, loading, error, refetch } = useQuery(AccessGroupPeopleDocument, {
    skip: term.length < 2 || !!person, fetchPolicy: "no-cache",
    variables: { input: { first: 20, where: { formattedName: {
      contains: term, caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive,
    } } } },
  });
  const personLink = (person: InspectionPerson) => <div className="min-w-0"><PersonLink id={`${id}-profile-${person.id}`} size="md" person={{ id: person.id,
    name: person.formattedName || t("admin.roles.unnamedPerson"), avatarContent: person.avatar?.content,
    avatarMime: person.avatar?.mimeType }} className="min-w-0 text-smycles-body" /><p className="ml-10 text-smycles-meta text-ink-muted">{person.emailPrimary}</p></div>;
  return <div className="min-w-0 space-y-3">
    <p className="smycles-field-label">{label}</p>
    {person ? <div className="flex flex-wrap items-center justify-between gap-3">{personLink(person)}
      <Button id={`${id}-change`} variant="secondary" size="compact" onClick={() => { onSelect(null); setSearch(""); setTerm(""); }}>
        {t("admin.inspection.changePerson")}</Button></div> : <div className="space-y-3">
      <TextInput id={`${id}-search`} type="search" value={search} aria-label={label} placeholder={t("admin.groups.searchPeople")}
        onChange={event => setSearch(event.target.value)} />
      {loading && <p role="status" className="text-smycles-body text-ink-muted">{t("common.loading")}</p>}
      {error && <div role="alert" className="space-y-2 text-smycles-body"><p>{userErrorMessage(intl, error)}</p>
        <Button variant="secondary" size="compact" onClick={() => void refetch().catch(() => {})}>{t("common.tryAgain")}</Button></div>}
      {!loading && search.trim() === term && term.length >= 2 && !error && data?.persons.nodes.filter(item => item.id !== excludedId).map(item =>
        <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 py-3">{personLink(item)}
          <Button id={`${id}-select-${item.id}`} variant="secondary" size="compact" onClick={() => onSelect(item)}>{t("admin.inspection.select")}</Button>
        </div>)}
      {!loading && search.trim() === term && term.length >= 2 && !error && data?.persons.nodes.filter(item => item.id !== excludedId).length === 0 &&
        <p className="text-smycles-body text-ink-muted">{t("admin.groups.noPeople")}</p>}
      {data?.persons.pageInfo.hasNextPage && <p className="text-smycles-meta text-ink-muted">{t("admin.groups.refineSearch")}</p>}
    </div>}
  </div>;
}
